import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
    type ReactNode,
} from 'react'
import { jwtDecode, type JwtPayload } from 'jwt-decode'
import { AUTH_DIALOG_URL, AUTH_LOGOUT_URL } from '../config'
import { api, apiErrorMessage, setAuthToken, setAuthTokenRefresher, type BolResponse } from '../lib/apiClient'
import { parseDialogMessage } from './messages'

const TOKEN_STORAGE_KEY = 'bol.accessToken'

/**
 * Treat a token as expired slightly early, so a request doesn't fail in flight over
 * a second or two of clock skew.
 */
const EXPIRY_SKEW_SECONDS = 60

interface BolAccessToken extends JwtPayload {
    agent_id?: number
    roles?: string[]
}

export type UserProfile = {
    id: number
    name: string
    initials: string
    email: string
    /** BOL2 has no role display name on /me; department is the human-readable label. */
    department: string | null
    agentId: number
    agentName: string
}

type AuthState = {
    status: 'loading' | 'signed-out' | 'signed-in'
    accessToken?: string
    roles: string[]
    profile?: UserProfile
    error?: string
    signIn: () => Promise<void>
    signOut: () => Promise<void>
}

const AuthContext = createContext<AuthState | null>(null)

export function useAuth(): AuthState {
    const context = useContext(AuthContext)
    if (!context) throw new Error('useAuth must be used within <OfficeAuthProvider>')
    return context
}

function initials(name: string): string {
    return name
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0]?.toUpperCase() ?? '')
        .join('')
}

function decode(token: string): BolAccessToken | null {
    try {
        return jwtDecode<BolAccessToken>(token)
    } catch {
        return null
    }
}

function isUsable(token: string): boolean {
    const claims = decode(token)
    if (!claims) return false
    // BOL2 issues `exp` as a formatted datetime string on some paths rather than the
    // numeric claim the JWT spec calls for, so accept both.
    const exp = typeof claims.exp === 'number' ? claims.exp : Date.parse(String(claims.exp)) / 1000
    if (!Number.isFinite(exp)) return true // no usable expiry — let the API decide
    return exp - EXPIRY_SKEW_SECONDS > Date.now() / 1000
}

function readStoredToken(): string | undefined {
    try {
        const token = window.localStorage.getItem(TOKEN_STORAGE_KEY)
        return token && isUsable(token) ? token : undefined
    } catch {
        return undefined
    }
}

function storeToken(token?: string): void {
    try {
        if (token) window.localStorage.setItem(TOKEN_STORAGE_KEY, token)
        else window.localStorage.removeItem(TOKEN_STORAGE_KEY)
    } catch {
        // Storage can be unavailable in some Outlook webviews; falling back to an
        // in-memory token just means re-authenticating next time the pane opens.
    }
}

/**
 * Opens BOL2 sign-in in an Office dialog and resolves with the resulting JWT.
 *
 * A dialog is required rather than doing this inline: the task pane is a
 * third-party iframe, so BOL2's SameSite=Lax `auth` cookie is not sent from it. The
 * dialog is its own top-level window on our origin, which is same-site with BOL2.
 */
function openSignInDialog(): Promise<string> {
    return new Promise((resolve, reject) => {
        Office.context.ui.displayDialogAsync(
            AUTH_DIALOG_URL,
            { height: 60, width: 30, promptBeforeOpen: false },
            (result) => {
                if (result.status !== Office.AsyncResultStatus.Succeeded) {
                    reject(new Error(result.error?.message ?? 'Could not open the sign-in window'))
                    return
                }

                const dialog = result.value
                let settled = false

                const finish = (fn: () => void) => {
                    if (settled) return
                    settled = true
                    dialog.close()
                    fn()
                }

                dialog.addEventHandler(Office.EventType.DialogMessageReceived, (arg) => {
                    const raw = (arg as { message?: string }).message ?? ''
                    const message = parseDialogMessage(raw)

                    if (!message) {
                        finish(() => reject(new Error('Unexpected response from the sign-in window')))
                        return
                    }
                    if (message.type === 'error') {
                        finish(() => reject(new Error(message.message)))
                        return
                    }
                    // Match on 'token' rather than treating everything non-error as one:
                    // the contract also carries 'signed-out', which is meaningless here.
                    if (message.type !== 'token') {
                        finish(() => reject(new Error('Unexpected response from the sign-in window')))
                        return
                    }
                    finish(() => resolve(message.token))
                })

                dialog.addEventHandler(Office.EventType.DialogEventReceived, (arg) => {
                    // 12006 is the user closing the dialog themselves — not an error worth surfacing.
                    const code = (arg as { error?: number }).error
                    if (settled) return
                    settled = true
                    reject(new Error(code === 12006 ? 'Sign-in cancelled' : 'The sign-in window closed unexpectedly'))
                })
            },
        )
    })
}

/**
 * Runs BOL2's logout in a dialog, so signing out clears the `auth` cookie and the
 * CodeIgniter session and not just our own JWT.
 *
 * Deliberately best-effort — it resolves whether or not the round trip succeeds. The
 * pane has already dropped its token by the time this runs, so the worst case is that
 * BOL2 still considers the webview signed in, which is not worth blocking the UI or
 * showing an error over.
 */
function openSignOutDialog(): Promise<void> {
    return new Promise((resolve) => {
        Office.context.ui.displayDialogAsync(
            AUTH_LOGOUT_URL,
            { height: 40, width: 25, promptBeforeOpen: false },
            (result) => {
                if (result.status !== Office.AsyncResultStatus.Succeeded) {
                    resolve()
                    return
                }

                const dialog = result.value
                let settled = false

                dialog.addEventHandler(Office.EventType.DialogMessageReceived, () => {
                    if (settled) return
                    settled = true
                    dialog.close()
                    resolve()
                })

                dialog.addEventHandler(Office.EventType.DialogEventReceived, () => {
                    // Covers the user closing the dialog themselves (12006). The dialog
                    // is already gone, so unlike the branch above it must not be closed.
                    if (settled) return
                    settled = true
                    resolve()
                })
            },
        )
    })
}

export function OfficeAuthProvider({ children }: { children: ReactNode }) {
    const [status, setStatus] = useState<AuthState['status']>('loading')
    const [accessToken, setAccessTokenState] = useState<string | undefined>()
    const [roles, setRoles] = useState<string[]>([])
    const [profile, setProfile] = useState<UserProfile | undefined>()
    const [error, setError] = useState<string | undefined>()

    const signInInFlight = useRef<Promise<string | undefined> | null>(null)

    const applyToken = useCallback((token: string) => {
        setAccessTokenState(token)
        setAuthToken(token)
        storeToken(token)
        setRoles(decode(token)?.roles ?? [])
    }, [])

    const clearToken = useCallback(() => {
        setAccessTokenState(undefined)
        setAuthToken(undefined)
        storeToken(undefined)
        setRoles([])
        setProfile(undefined)
    }, [])

    /** Runs the dialog at most once at a time; shared with the axios 401 handler. */
    const requestToken = useCallback(async (): Promise<string | undefined> => {
        signInInFlight.current ??= (async () => {
            try {
                const token = await openSignInDialog()
                applyToken(token)
                setError(undefined)
                return token
            } catch (dialogError) {
                clearToken()
                setStatus('signed-out')
                setError(dialogError instanceof Error ? dialogError.message : 'Sign-in failed')
                return undefined
            } finally {
                signInInFlight.current = null
            }
        })()

        return signInInFlight.current
    }, [applyToken, clearToken])

    const signIn = useCallback(async () => {
        setError(undefined)
        setStatus('loading')
        const token = await requestToken()
        if (!token) return
        setStatus('signed-in')
    }, [requestToken])

    const signOut = useCallback(async () => {
        // Clear locally first so the pane reacts immediately, then let BOL2 clear the
        // cookie and CI session. Doing only the local half would leave the user signed
        // in to BOL2 — and a stale cookie beside a live CI session is the state the
        // sign-in dialog cannot recover from, so the sign-out would wedge the add-in.
        clearToken()
        setStatus('signed-out')
        setError(undefined)
        await openSignOutDialog()
    }, [clearToken])

    // Let the axios interceptor drive the same single-flight dialog on a 401.
    useEffect(() => {
        setAuthTokenRefresher(requestToken)
        return () => setAuthTokenRefresher(undefined)
    }, [requestToken])

    // Restore a cached token on open so the common case needs no dialog at all.
    useEffect(() => {
        const stored = readStoredToken()
        if (stored) {
            applyToken(stored)
            setStatus('signed-in')
        } else {
            setStatus('signed-out')
        }
    }, [applyToken])

    // Load the profile that fills the pane header.
    useEffect(() => {
        if (!accessToken) return
        let cancelled = false

        void (async () => {
            try {
                const { data } = await api.get<
                    BolResponse<{
                        id: number
                        name: string
                        email: string
                        department: string | null
                        agent: { id: number; name: string }
                    }>
                >('/me')

                if (cancelled) return

                setProfile({
                    id: data.data.id,
                    name: data.data.name,
                    initials: initials(data.data.name),
                    email: data.data.email,
                    department: data.data.department,
                    agentId: data.data.agent.id,
                    agentName: data.data.agent.name,
                })
                setStatus('signed-in')
            } catch (profileError) {
                if (cancelled) return
                setError(apiErrorMessage(profileError, 'Could not load your Blocks Online profile'))
            }
        })()

        return () => {
            cancelled = true
        }
    }, [accessToken])

    const value = useMemo<AuthState>(
        () => ({ status, accessToken, roles, profile, error, signIn, signOut }),
        [status, accessToken, roles, profile, error, signIn, signOut],
    )

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
